<script lang="ts" setup>
import { reactive, ref, watch, computed, onMounted, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import type { CountryData } from 'country-codes-list'
import type { ImportAccountDetails } from '@/types'
import { useGeo, usePostcodeState } from '@/composables'
import { useDirtyFormGuard } from '@/composables/useDirtyFormGuard'
import { getAllCountryCodesData } from '@/composables/useGeo'
import { formatPhone, parsePastedPhone } from '@/utils/phone'

const emit = defineEmits<{
  change: [formData: ImportAccountDetails]
}>()

const { t } = useI18n()

// Geo data for countries and subdivisions
const {
  countries,
  subdivisions,
  loadCountries,
  loadSubdivisions,
  subdivisionsLoading
} = useGeo()

// Phone country code handling
const countryCodes = ref<CountryData[]>([])
const phoneCountryData = ref<CountryData | null>(null)
const phoneLocal = ref('')
const knownCallingCodes = computed(() => countryCodes.value.map((c) => c.countryCallingCode))

const form = reactive({
  email: '',
  name: '',
  surname: '',
  org: '',
  line1: '',
  line2: '',
  city: '',
  postcode: '',
  country_iso: 'DE',
  state: '',
  ust_id: ''
})

// Track dirty state - form is dirty when any required fields have content
const isDirty = computed(() => {
  return !!(
    form.email.trim() ||
    form.name.trim() ||
    form.surname.trim() ||
    form.line1.trim() ||
    form.city.trim() ||
    form.postcode.trim() ||
    phoneLocal.value.trim()
  )
})
useDirtyFormGuard('migration-account', isDirty)

// State suggestion from postcode
const stateRef = toRef(() => form.state)
const countryRef = toRef(() => form.country_iso)
const cityRef = toRef(() => form.city)
const { fetchStateFromPostcode } = usePostcodeState(stateRef, {
  onlyWhenEmpty: true,
  countryRef,
  cityRef,
  useCode: true
})

// Load countries on mount and initial subdivisions for DE
onMounted(() => {
  loadCountries()
  loadSubdivisions('DE')
  countryCodes.value = getAllCountryCodesData()
  phoneCountryData.value = countryCodes.value.find((c) => c.countryCode === 'DE') || null
})

// Load subdivisions when country changes
watch(
  () => form.country_iso,
  (newCountry, oldCountry) => {
    if (newCountry) {
      loadSubdivisions(newCountry)
      form.state = ''
      // Sync phone country code with address country
      if (oldCountry !== undefined && oldCountry !== newCountry && countryCodes.value.length > 0) {
        const match = countryCodes.value.find((c) => c.countryCode === newCountry)
        if (match) phoneCountryData.value = match
      }
    }
  }
)

// Detect international format on phone input
const handlePhoneInput = () => {
  const input = phoneLocal.value
  if (input.startsWith('+') || input.startsWith('00')) {
    const currentCode = '+' + (phoneCountryData.value?.countryCallingCode || '49')
    const { countryCode, local } = parsePastedPhone(input, currentCode, knownCallingCodes.value)
    const match = countryCodes.value.find(
      (c) => '+' + c.countryCallingCode === countryCode
    )
    if (match) phoneCountryData.value = match
    phoneLocal.value = local
  }
}

// Detect international format on paste
const handlePhonePaste = (event: ClipboardEvent) => {
  const pasted = event.clipboardData?.getData('text') || ''
  if (pasted.startsWith('+') || pasted.startsWith('00') || /^\d{10,}$/.test(pasted.trim())) {
    event.preventDefault()
    const currentCode = '+' + (phoneCountryData.value?.countryCallingCode || '49')
    const { countryCode, local } = parsePastedPhone(pasted, currentCode, knownCallingCodes.value)
    const match = countryCodes.value.find(
      (c) => '+' + c.countryCallingCode === countryCode
    )
    if (match) phoneCountryData.value = match
    phoneLocal.value = local
  }
}

const errors = reactive<Record<string, string>>({})

// Validate a single field
const validateField = (field: string): boolean => {
  delete errors[field]

  switch (field) {
    case 'email':
      if (!form.email) {
        errors.email = t('validation.required')
        return false
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        errors.email = t('validation.email')
        return false
      }
      break
    case 'line1':
      if (!form.line1) {
        errors.line1 = t('validation.required')
        return false
      }
      break
    case 'city':
      if (!form.city) {
        errors.city = t('validation.required')
        return false
      }
      break
    case 'postcode':
      if (!form.postcode) {
        errors.postcode = t('validation.required')
        return false
      }
      break
    case 'country_iso':
      if (!form.country_iso || form.country_iso.length !== 2) {
        errors.country_iso = t('validation.required')
        return false
      }
      break
    case 'phone':
      if (!phoneLocal.value) {
        errors.phone = t('validation.required')
        return false
      }
      break
  }

  return true
}

// Check if form is valid
const isValid = computed(() => {
  return (
    form.email &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    form.line1 &&
    form.city &&
    form.postcode &&
    form.country_iso &&
    form.country_iso.length === 2 &&
    phoneLocal.value
  )
})

// Emit changes when form is valid
watch(
  [() => ({ ...form }), phoneLocal, phoneCountryData],
  () => {
    if (isValid.value) {
      const phoneCode = '+' + (phoneCountryData.value?.countryCallingCode || '49')
      const formattedPhone = formatPhone(phoneCode, phoneLocal.value)

      const formData: ImportAccountDetails = {
        email: form.email,
        line1: form.line1,
        city: form.city,
        postcode: form.postcode,
        country_iso: form.country_iso.toUpperCase(),
        phone: formattedPhone
      }
      // Add optional fields if filled
      if (form.name) formData.name = form.name
      if (form.surname) formData.surname = form.surname
      if (form.org) formData.org = form.org
      if (form.line2) formData.line2 = form.line2
      if (form.state) formData.state = form.state
      if (form.ust_id) formData.ust_id = form.ust_id

      emit('change', formData)
    }
  },
  { deep: true }
)
</script>

<template>
  <div class="account-form">
    <!-- Contact Info Row -->
    <div class="form-row">
      <div class="form-group">
        <label class="form-label required">
          {{ $t('dashboard.admin.migration.accountForm.email') }}
        </label>
        <input
          v-model="form.email"
          type="email"
          class="form-input"
          :class="{ error: errors.email }"
          :placeholder="$t('dashboard.admin.migration.accountForm.emailPlaceholder')"
          @blur="validateField('email')"
        />
        <span v-if="errors.email" class="error-text">{{ errors.email }}</span>
      </div>
    </div>

    <!-- Name Row -->
    <div class="form-row two-col">
      <div class="form-group">
        <label class="form-label">
          {{ $t('dashboard.admin.migration.accountForm.name') }}
        </label>
        <input
          v-model="form.name"
          type="text"
          class="form-input"
          :placeholder="$t('dashboard.admin.migration.accountForm.namePlaceholder')"
        />
      </div>
      <div class="form-group">
        <label class="form-label">
          {{ $t('dashboard.admin.migration.accountForm.surname') }}
        </label>
        <input
          v-model="form.surname"
          type="text"
          class="form-input"
          :placeholder="$t('dashboard.admin.migration.accountForm.surnamePlaceholder')"
        />
      </div>
    </div>

    <!-- Organization -->
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">
          {{ $t('dashboard.admin.migration.accountForm.organization') }}
        </label>
        <input
          v-model="form.org"
          type="text"
          class="form-input"
          :placeholder="$t('dashboard.admin.migration.accountForm.organizationPlaceholder')"
        />
      </div>
    </div>

    <!-- Address -->
    <div class="form-row">
      <div class="form-group">
        <label class="form-label required">
          {{ $t('dashboard.admin.migration.accountForm.street') }}
        </label>
        <input
          v-model="form.line1"
          type="text"
          class="form-input"
          :class="{ error: errors.line1 }"
          :placeholder="$t('dashboard.admin.migration.accountForm.streetPlaceholder')"
          @blur="validateField('line1')"
        />
        <span v-if="errors.line1" class="error-text">{{ errors.line1 }}</span>
      </div>
    </div>

    <div class="form-row">
      <div class="form-group">
        <label class="form-label">
          {{ $t('dashboard.admin.migration.accountForm.addressLine2') }}
        </label>
        <input
          v-model="form.line2"
          type="text"
          class="form-input"
          :placeholder="$t('dashboard.admin.migration.accountForm.addressLine2Placeholder')"
        />
      </div>
    </div>

    <!-- Country & State -->
    <div class="form-row two-col">
      <div class="form-group">
        <label class="form-label required">
          {{ $t('dashboard.admin.migration.accountForm.country') }}
        </label>
        <select
          v-model="form.country_iso"
          class="form-input"
          :class="{ error: errors.country_iso }"
          @blur="validateField('country_iso')"
        >
          <option value="" disabled>{{ $t('dashboard.admin.migration.accountForm.countrySelect') }}</option>
          <option v-for="country in countries" :key="country.code" :value="country.code">
            {{ country.flag }} {{ country.name }}
          </option>
        </select>
        <span v-if="errors.country_iso" class="error-text">{{ errors.country_iso }}</span>
      </div>
      <div class="form-group">
        <label class="form-label">
          {{ $t('dashboard.admin.migration.accountForm.state') }}
        </label>
        <select
          v-model="form.state"
          class="form-input"
          :disabled="subdivisionsLoading || subdivisions.length === 0"
        >
          <option value="">
            {{ subdivisionsLoading ? 'Laden...' : $t('dashboard.admin.migration.accountForm.stateSelect') }}
          </option>
          <option v-for="subdivision in subdivisions" :key="subdivision.code" :value="subdivision.code">
            {{ subdivision.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- City & Postcode -->
    <div class="form-row two-col">
      <div class="form-group">
        <label class="form-label required">
          {{ $t('dashboard.admin.migration.accountForm.postcode') }}
        </label>
        <input
          v-model="form.postcode"
          type="text"
          class="form-input"
          :class="{ error: errors.postcode }"
          :placeholder="$t('dashboard.admin.migration.accountForm.postcodePlaceholder')"
          @blur="validateField('postcode'); fetchStateFromPostcode(form.postcode)"
        />
        <span v-if="errors.postcode" class="error-text">{{ errors.postcode }}</span>
      </div>
      <div class="form-group">
        <label class="form-label required">
          {{ $t('dashboard.admin.migration.accountForm.city') }}
        </label>
        <input
          v-model="form.city"
          type="text"
          class="form-input"
          :class="{ error: errors.city }"
          :placeholder="$t('dashboard.admin.migration.accountForm.cityPlaceholder')"
          @blur="validateField('city')"
        />
        <span v-if="errors.city" class="error-text">{{ errors.city }}</span>
      </div>
    </div>

    <!-- Phone -->
    <div class="form-row">
      <div class="form-group" :class="{ error: errors.phone }">
        <label class="form-label required">
          {{ $t('dashboard.admin.migration.accountForm.phone') }}
        </label>
        <div class="phone-input-wrapper">
          <select
            :value="phoneCountryData?.countryCallingCode"
            class="form-input phone-code-select"
            @change="phoneCountryData = countryCodes.find((c) => c.countryCallingCode === ($event.target as HTMLSelectElement).value) || null"
          >
            <option v-for="cc in countryCodes" :key="cc.countryCode" :value="cc.countryCallingCode">
              {{ cc.flag }} +{{ cc.countryCallingCode }}
            </option>
          </select>
          <input
            v-model="phoneLocal"
            type="tel"
            class="form-input phone-local"
            :class="{ error: errors.phone }"
            placeholder="27613073"
            @input="handlePhoneInput"
            @paste="handlePhonePaste"
            @blur="validateField('phone')"
          />
        </div>
        <span v-if="errors.phone" class="error-text">{{ errors.phone }}</span>
      </div>
    </div>

    <!-- VAT ID -->
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">
          {{ $t('dashboard.admin.migration.accountForm.vatId') }}
        </label>
        <input
          v-model="form.ust_id"
          type="text"
          class="form-input"
          :placeholder="$t('dashboard.admin.migration.accountForm.vatIdPlaceholder')"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.account-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  overflow: hidden;
}

.form-row {
  display: flex;
  gap: 12px;
  min-width: 0;

  &.two-col {
    .form-group {
      flex: 1;
      min-width: 0;
    }
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.form-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.7);

  &.required::after {
    content: ' *';
    color: #e57373;
  }
}

.form-input {
  padding: 10px 12px;
  background: rgba(15, 23, 48, 0.6);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  outline: none;
  transition: all 0.15s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    border-color: rgba(120, 163, 255, 0.35);
    background: rgba(15, 23, 48, 0.8);
  }

  &.error {
    border-color: rgba(244, 67, 54, 0.5);
    background: rgba(244, 67, 54, 0.05);
  }
}

.phone-input-wrapper {
  display: flex;
  gap: 8px;
}

.phone-code-select {
  width: 120px;
  flex-shrink: 0;
}

.phone-local {
  flex: 1;
  min-width: 0;
}

.error-text {
  font-size: 0.75rem;
  color: #e57373;
}

/* Light Theme */
.v-theme--lightTheme {
  .form-label {
    color: rgba(15, 23, 42, 0.7);
  }

  .form-input {
    background: rgba(255, 255, 255, 0.8);
    color: rgba(15, 23, 42, 0.9);

    &::placeholder {
      color: rgba(15, 23, 42, 0.4);
    }
  }
}

@media (max-width: 480px) {
  .form-row.two-col {
    flex-direction: column;
  }
}
</style>
